// src/components/pages/Generator.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import AppShell from "../layout/AppShell";
import Button from "../ui/Button";
import Card from "../ui/Card";
import Ball, { BallsRow } from "../ui/Ball";
import Countdown, { NEXT_MEGA_MILLIONS, NEXT_POWERBALL } from "../ui/Countdown";
import Icon from "../ui/Icon";
import { Toast } from "../ui/Spinner";
import ConfirmDialog from "../ui/ConfirmDialog";

import axiosInstance from "../../utils/AxiosInstance";
import {
  LOTTERY,
  LOTTERY_META,
  GAMES_PER_CREDIT,
  normalizeLotteryType,
  getSpecialBall,
  lotteryName,
} from "../../utils/lottery";
import { getNextDraws, formatJackpot } from "../../services/resultsService";

export default function Generator() {
  const navigate = useNavigate();

  const [lotteryType, setLotteryType] = useState(LOTTERY.MEGA_MILLIONS);
  const [quantity, setQuantity] = useState(1);
  const [credits, setCredits] = useState(0);
  const [generated, setGenerated] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ open: false, tone: "success", message: "" });
  const [nextDraws, setNextDraws] = useState({});

  // Review-before-generate modal. `reviewAction` distinguishes the first
  // generation from a regenerate so the modal copy/button label can match.
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState("generate"); // "generate" | "regenerate"

  // Scroll target for the results panel so the user is taken straight to
  // their new numbers once generation finishes.
  const resultsRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get("/site/user/logged-user");
        if (res.data?.id) setCredits(res.data?.account?.credit ?? 0);
      } catch { /* ProtectedRoute handles auth */ }
    })();
  }, []);

  // Authoritative next-draw date + jackpot from the backend (public
  // endpoint). Falls back to the client-side estimate below if it fails.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await getNextDraws();
      if (!cancelled) setNextDraws(data);
    })();
    return () => { cancelled = true; };
  }, []);

  // Once numbers land, bring the results panel into view so the user
  // doesn't have to scroll down manually to see what was generated.
  useEffect(() => {
    if (generated.length > 0) {
      resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [generated]);

  const showToast = (message, tone = "success") => {
    setToast({ open: true, tone, message });
    setTimeout(() => setToast((t) => ({ ...t, open: false })), 3500);
  };

  const runGenerate = async () => {
    if (generating) return;
    setError(null);
    setGenerated([]);

    const MAX_CREDITS_PER_GENERATE = 20; // must match UI stepper upper bound
    const creditsToSpend = Math.min(
      MAX_CREDITS_PER_GENERATE,
      Math.max(1, Math.floor(Number(quantity)) || 1)
    );
    if (credits < creditsToSpend) {
      setError({
        type: "INSUFFICIENT_CREDITS",
        message: `You have ${credits} credit${credits === 1 ? "" : "s"}, but this run needs ${creditsToSpend}.`,
      });
      return;
    }

    setGenerating(true);
    try {
      const normalized = normalizeLotteryType(lotteryType);
      const buyRes = await axiosInstance.post("/site/buy-draw", {
        typeLottery: normalized,
        creditUsed: creditsToSpend,
      });
      const buyDrawId = buyRes?.data?.id;
      if (!buyDrawId) throw new Error("Missing buy-draw id from server.");

      const analyzerRes = await axiosInstance.post(
        `/site/lotto-analyzer/get-next-winning-numbers/${encodeURIComponent(buyDrawId)}`,
        {
          typeLottery: normalized,
          numOfGames: creditsToSpend * GAMES_PER_CREDIT,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      // The backend has shipped 4 different response shapes for this
      // endpoint over time. Mirror the original Dashboard.jsx extractor
      // so all of them work:
      //   1. { winningNumbers: [...] }   — camelCase
      //   2. { winningnumbers: [...] }   — all-lowercase  ← MISSED previously
      //   3. { winning_numbers: [...] }  — snake_case
      //   4. [...]                       — bare array
      const data = analyzerRes?.data;
      const raw =
        (data && Array.isArray(data.winningNumbers))  ? data.winningNumbers  :
        (data && Array.isArray(data.winningnumbers))  ? data.winningnumbers  :
        (data && Array.isArray(data.winning_numbers)) ? data.winning_numbers :
        (Array.isArray(data))                         ? data                 :
        [];

      if (Array.isArray(raw) && raw.length > 0) {
        setGenerated(raw);
        // Optimistic local update so the balance reflects the spend
        // immediately, before we hear back from the server.
        setCredits((c) => Math.max(0, c - creditsToSpend));
        try {
          const meRes = await axiosInstance.get("/site/user/logged-user");
          const serverCredit =
            meRes?.data?.id ? meRes?.data?.account?.credit : null;
          if (typeof serverCredit === "number") {
            // Defensive reconciliation against stale GET responses.
            //
            // The browser HTTP cache (and any CDN in front of the API)
            // can serve this endpoint from cache, so the value coming
            // back may pre-date the buy-draw we just made. If the
            // server reports a higher balance than our optimistic
            // value, it's almost certainly stale — keep the optimistic
            // value. If it's the same or lower (e.g. another tab spent
            // a credit), trust the server. The AxiosInstance also adds
            // a `_ts` cache-buster to GETs, but this guard means a
            // missed cache header in any one path can never flash the
            // user back to a "credits didn't move" state.
            setCredits((c) => Math.min(c, serverCredit));
          }
        } catch { /* ignore — optimistic value already shown */ }
      } else {
        setError({ type: "GENERIC", message: "No numbers returned. Try again." });
      }
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      const msg = typeof data === "string" ? data : data?.message || data?.error || err.message || "";
      if (status === 400 && /credit|insufficient|balance|fund/i.test(String(msg))) {
        setError({
          type: "INSUFFICIENT_CREDITS",
          message: "Insufficient credits. Buy more to keep generating.",
        });
      } else {
        setError({
          type: "GENERIC",
          message: msg || "Something went wrong. Please try again.",
        });
      }
    } finally {
      setGenerating(false);
    }
  };

  // Opens the review modal instead of generating directly. Used by both
  // the initial "Generate" button and the "Regenerate" button so a spend
  // is never made without the user seeing exactly what it will cost.
  const requestGenerate = () => {
    if (generating) return;
    setError(null);
    setReviewAction("generate");
    setReviewOpen(true);
  };

  const requestRegenerate = () => {
    if (generating) return;
    setReviewAction("regenerate");
    setReviewOpen(true);
  };

  const confirmReview = async () => {
    setReviewOpen(false);
    await runGenerate();
  };

  const copyAll = async () => {
    if (!generated.length) return;
    const lines = generated.map((g) => {
      const main = [g.num1, g.num2, g.num3, g.num4, g.num5].join(" - ");
      const special = getSpecialBall(g);
      const specialLabel = LOTTERY_META[lotteryType]?.specialLabel || "Special";
      return `${main}  |  ${specialLabel}: ${special}`;
    });
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      showToast("Copied to clipboard.", "success");
    } catch {
      showToast("Copy failed. Select the numbers manually.", "error");
    }
  };

  const fallbackTarget =
    lotteryType === LOTTERY.MEGA_MILLIONS ? NEXT_MEGA_MILLIONS() : NEXT_POWERBALL();
  const target = nextDraws[lotteryType]?.target || fallbackTarget;
  const meta = LOTTERY_META[lotteryType];

  // Mirrors the clamp inside runGenerate so the review modal always shows
  // the exact credit/game counts that will actually be charged.
  const reviewCreditsToSpend = Math.min(20, Math.max(1, Math.floor(Number(quantity)) || 1));
  const reviewGamesCount = reviewCreditsToSpend * GAMES_PER_CREDIT;

  return (
    <AppShell>
      <Toast open={toast.open} tone={toast.tone} message={toast.message} />

      <div className="px-4 sm:px-6 py-6 max-w-[680px] mx-auto">

        <h1 className="font-semibold mb-1" style={{ fontSize: 26, color: "var(--txt)" }}>
          Generate your pick.
        </h1>
        <p className="text-[13px] mb-5" style={{ color: "var(--txt-2)" }}>
          Pick a lottery — our model does the rest.
        </p>

        <Card variant="teal" className="flex items-center justify-between mb-4">
          <span className="flex items-center gap-2 text-[12px] font-medium" style={{ color: "var(--teal)" }}>
            <Icon name="coin" size={14} />
            {credits} credit{credits === 1 ? "" : "s"} remaining
          </span>
          <Button size="sm" variant="outline" onClick={() => navigate("/plans")}>
            Top up
            <Icon name="arrow-right" size={14} />
          </Button>
        </Card>

        <div className="lg-label mb-2.5">Choose lottery</div>
        <div className="grid grid-cols-2 gap-3 mb-5">
          <LotteryCard
            type={LOTTERY.MEGA_MILLIONS}
            jackpot={nextDraws.MEGA_MILLIONS?.value != null ? formatJackpot(nextDraws.MEGA_MILLIONS.value) : "$340M"}
            selected={lotteryType === LOTTERY.MEGA_MILLIONS}
            onClick={() => setLotteryType(LOTTERY.MEGA_MILLIONS)}
          />
          <LotteryCard
            type={LOTTERY.POWER_BALL}
            jackpot={nextDraws.POWER_BALL?.value != null ? formatJackpot(nextDraws.POWER_BALL.value) : "$120M"}
            selected={lotteryType === LOTTERY.POWER_BALL}
            onClick={() => setLotteryType(LOTTERY.POWER_BALL)}
          />
        </div>

        {/* ----- Stepper (with explicit +/- icons) ----- */}
        <div className="lg-label mb-2.5">How many credits to spend?</div>
        <Card variant="soft" className="mb-5">
          <div className="flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              disabled={quantity <= 1}
              className="w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-40 transition-colors"
              style={{
                background: "var(--surf-3)",
                color: "var(--txt)",
                border: "1px solid var(--border-2)",
              }}
              aria-label="Decrease"
            >
              <Icon name="minus" size={16} />
            </button>

            <div className="text-center">
              <div className="font-semibold" style={{ fontSize: 32, color: "var(--gold)" }}>
                {quantity}
              </div>
              <div className="text-[11px]" style={{ color: "var(--txt-2)" }}>
                = {quantity * GAMES_PER_CREDIT} games
              </div>
            </div>

            <button
              type="button"
              onClick={() => setQuantity((q) => Math.min(20, q + 1))}
              disabled={quantity >= 20 || quantity >= credits}
              className="w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-40 transition-colors"
              style={{
                background: "var(--surf-3)",
                color: "var(--txt)",
                border: "1px solid var(--border-2)",
              }}
              aria-label="Increase"
            >
              <Icon name="plus" size={16} />
            </button>
          </div>
        </Card>

        {/* Generic model info (no strategy reveal) */}
        <Card variant="soft" className="mb-5">
          <div className="flex items-start gap-3">
            <Icon
              name="brain"
              size={22}
              style={{ color: "var(--gold)", flexShrink: 0, marginTop: 2 }}
            />
            <div>
              <div className="font-medium text-[13px]" style={{ color: "var(--txt)" }}>
                Trained on real draws — refreshed for every drawing.
              </div>
              <p className="text-[12px] mt-1 leading-relaxed" style={{ color: "var(--txt-2)" }}>
                Our model has been trained on 20+ years of real Mega Millions
                and Powerball results, and is refreshed before each draw so
                your picks reflect the latest data.
              </p>
            </div>
          </div>
        </Card>

        {/* Error display */}
        {error && (
          <Card variant={error.type === "INSUFFICIENT_CREDITS" ? "gold" : "red"} className="mb-4">
            <div className="flex items-start gap-3">
              <Icon
                name="alert-circle"
                size={20}
                style={{
                  color: error.type === "INSUFFICIENT_CREDITS" ? "var(--gold)" : "var(--red)",
                  flexShrink: 0,
                }}
              />
              <div className="flex-1">
                <p className="text-[13px]" style={{
                  color: error.type === "INSUFFICIENT_CREDITS" ? "var(--gold)" : "var(--red)",
                }}>
                  {error.message}
                </p>
                {error.type === "INSUFFICIENT_CREDITS" && (
                  <Button size="sm" className="mt-3" onClick={() => navigate("/plans")}>
                    <Icon name="coin" size={14} />
                    Buy credits
                  </Button>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Generate button */}
        <div className="text-center mb-5">
          <Button
            size="lg"
            full
            loading={generating}
            disabled={credits < quantity}
            onClick={requestGenerate}
          >
            {!generating && <Icon name="sparkles" size={16} />}
            {generating ? "Generating..." : `Generate ${quantity * GAMES_PER_CREDIT} games`}
          </Button>
          <p className="text-[11px] mt-3" style={{ color: "var(--txt-3)" }}>
            Next {lotteryName(lotteryType)} draw in <Countdown target={target} compact />
          </p>
        </div>

        {/* Results */}
        {generated.length > 0 && (
          <div ref={resultsRef}>
          <Card className="mb-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="lg-label mb-1">Your numbers</div>
                <div className="text-[12px]" style={{ color: "var(--txt-2)" }}>
                  {generated.length} game{generated.length === 1 ? "" : "s"} · {lotteryName(lotteryType)}
                </div>
              </div>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium"
                    style={{
                      background: "var(--teal-dim)",
                      color: "var(--teal)",
                      border: "1px solid var(--teal-border)",
                    }}>
                <Icon name="circle-check" size={11} />
                Saved
              </span>
            </div>

            <div className="space-y-3">
              {generated.map((g, i) => (
                <div
                  key={i}
                  className="py-3"
                  style={{
                    borderBottom:
                      i < generated.length - 1 ? "1px solid var(--border)" : "none",
                  }}
                >
                  <div className="text-[11px] mb-2" style={{ color: "var(--txt-3)" }}>
                    Game {i + 1}
                  </div>
                  <BallsRow>
                    {[g.num1, g.num2, g.num3, g.num4, g.num5].map((n, j) => (
                      <Ball
                        key={`g${i}-${j}`}
                        value={n}
                        size="md"
                        animate
                        delay={i * 80 + j * 60}
                      />
                    ))}
                    <Ball
                      value={getSpecialBall(g)}
                      size="md"
                      variant="gold"
                      animate
                      delay={i * 80 + 5 * 60}
                    />
                  </BallsRow>
                </div>
              ))}
            </div>

            <div className="flex gap-2 mt-4">
              <Button variant="outline" full onClick={copyAll}>
                <Icon name="copy" size={14} />
                Copy all
              </Button>
              <Button variant="outline" full onClick={requestRegenerate} loading={generating}>
                {!generating && <Icon name="refresh" size={14} />}
                Regenerate
              </Button>
            </div>
            <p className="text-[11px] mt-3 text-center" style={{ color: "var(--txt-3)" }}>
              Green ball = {meta.specialLabel}. Your picks are automatically saved to your account.
            </p>
          </Card>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={reviewOpen}
        title={reviewAction === "regenerate" ? "Confirm regeneration" : "Confirm generation"}
        message={
          reviewAction === "regenerate"
            ? "Regenerating replaces your current numbers and spends credits again — review before continuing."
            : "Review the details below before we spend your credits and generate your numbers."
        }
        confirmLabel={reviewAction === "regenerate" ? "Regenerate" : "Generate"}
        cancelLabel="Cancel"
        icon="sparkles"
        loading={generating}
        onConfirm={confirmReview}
        onClose={() => setReviewOpen(false)}
      >
        <div
          className="rounded-[var(--r-lg)] p-3 space-y-2"
          style={{ background: "var(--surf-2)", border: "1px solid var(--border)" }}
        >
          <SummaryRow label="Lottery" value={lotteryName(lotteryType)} />
          <SummaryRow label="Credits used" value={`${reviewCreditsToSpend} credit${reviewCreditsToSpend === 1 ? "" : "s"}`} />
          <SummaryRow label="Games being generated" value={`${reviewGamesCount} games`} />
        </div>
      </ConfirmDialog>
    </AppShell>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-[13px]">
      <span style={{ color: "var(--txt-2)" }}>{label}</span>
      <span className="font-medium" style={{ color: "var(--txt)" }}>{value}</span>
    </div>
  );
}

function LotteryCard({ type, jackpot, selected, onClick }) {
  const meta = LOTTERY_META[type];
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative text-left p-4 rounded-[var(--r-lg)] transition-all"
      style={{
        background: selected ? "var(--gold-dim)" : "var(--surf)",
        border: `1px solid ${selected ? "var(--gold-border)" : "var(--border)"}`,
      }}
    >
      <div className="flex items-start justify-between mb-3">
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center"
          style={{
            background: selected ? "var(--gold)" : "transparent",
            border: `2px solid ${selected ? "var(--gold)" : "var(--txt-3)"}`,
          }}
        >
          {selected && (
            <span style={{
              width: 8, height: 8, borderRadius: "50%", background: "#0E2A18",
            }} />
          )}
        </div>
        <Icon
          name={meta.icon || "star"}
          size={18}
          style={{ color: selected ? "var(--gold)" : "var(--txt-3)" }}
        />
      </div>
      <div className="lg-label mb-1">{meta.label}</div>
      <div className="font-semibold mb-1" style={{
        fontSize: 22, color: selected ? "var(--gold)" : "var(--txt)",
      }}>
        {jackpot}
      </div>
      <div className="text-[11px]" style={{ color: "var(--txt-2)" }}>
        {meta.schedule}
      </div>
    </button>
  );
}